// translationService.ts

export type Lang = 'en' | 'es' | 'ja';

interface Translation {
  name: string;
  infoTitle: string;
  jobTitle: string;
  aboutText1: string;
  aboutText2: string;
  aboutText3: string;
  emailPress: string;
  email: string;
  tempUnit: string;
  contactFormTitle: string;
  nameLabel: string;
  pronounsLabel: string;
  emailLabel: string;
  descriptionLabel: string;
  submitButton: string;
  cancelButton: string;
  read: string;
  listen: string;
  sendingLabel: string;
  sendError: string;
  validationRequired: string;
  validationEmail: string;
}

interface Translations {
  en: Translation & { weatherConditions: { [key: string]: string } };
  es: Translation & { weatherConditions: { [key: string]: string } };
  ja: Translation & { weatherConditions: { [key: string]: string } };
}

const translations: Translations = {
  en: {
    name: "Unique Sada",
    infoTitle: "INFO",
    jobTitle: "Architecture, Design, Spatial Diversity",
    aboutText1:
      "Unique Sada’s practice unfolds at the intersections of spatial design, cultural memory, and collective autonomy. Rooted in the belief that the built environment is never neutral, her work seeks to decolonize architecture and urban planning, dismantling white spatial logics while re-centering Indigenous ecologies and communities of color.",
    aboutText2:
      "Drawing from traditions of cultural preservation and social connectivity, she treats space not simply as physical infrastructure but as a living archive of histories, desires, and futures. In reimagining urban fabric, she examines how design can transcend material boundaries, moving through space, architecture, and everyday practices, to shape belonging and repair.",
    aboutText3:
      "Unique is currently pursuing a Master of Architecture in Design Practice at the Royal College of Art, with a focus on material experimentation and design innovation. She also holds a Master’s in Urban Planning, with a concentration in Design of the Built Environment, from the University of Southern California. Her work spans public and private sectors, from large-scale developments to intimate spaces, carrying intention and reverence into every project.",
    emailPress: "CONTACT / PRESS",
    email: "contact@unique-sada.works",
    tempUnit: "°C",
    contactFormTitle: "Contact Unique.",
    nameLabel: "Name",
    pronounsLabel: "Pronouns",
    emailLabel: "Email",
    descriptionLabel: "Description",
    submitButton: "Submit",
    cancelButton: "Cancel",
    // Weather keys are LOWERCASE — always look up with a normalized key.
    weatherConditions: {
      "clear": "Clear",
      "sunny": "Sunny",
      "partly cloudy": "Partly cloudy",
      "cloudy": "Cloudy",
      "overcast": "Overcast",
      "mist": "Mist",
      "patchy rain possible": "Patchy rain possible",
      "light rain": "Light rain",
      "light rain shower": "Light rain",
      "patchy light rain": "Light rain",
    },
    read: "READ",
    listen: "LISTEN",
    sendingLabel: "Sending…",
    sendError: "Something went wrong sending your message. Please try again.",
    validationRequired: "Please fill in your name, email, and message.",
    validationEmail: "Please enter a valid email address.",
  },

  es: {
    name: "Unique Sada",
    infoTitle: "INFORMACIÓN",
    jobTitle: "Arquitectura, diseño y diversidad espacial",
    aboutText1:
      "La práctica de Unique Sada se desarrolla en la intersección del diseño espacial, la memoria cultural y la autonomía colectiva. Partiendo de la convicción de que el entorno construido nunca es neutral, su trabajo busca descolonizar la arquitectura y la planificación urbana, desmantelando las lógicas espaciales hegemónicas y revalorizando las ecologías indígenas y las comunidades racializadas.",
    aboutText2:
      "Inspirada en tradiciones de preservación cultural y de conexión social, concibe el espacio no solo como infraestructura física, sino como un archivo vivo de historias, deseos y futuros. Al reimaginar el tejido urbano, explora cómo el diseño puede trascender los límites materiales, atravesando el espacio, la arquitectura y las prácticas cotidianas para generar pertenencia y reparación.",
    aboutText3:
      "Actualmente, Unique cursa un Máster en Arquitectura en Práctica del Diseño en el Royal College of Art, con un enfoque en la experimentación material y la innovación en el diseño. También posee un Máster en Planificación Urbana, con concentración en Diseño del Entorno Construido, de la Universidad del Sur de California. Su trabajo abarca los sectores público y privado, desde grandes desarrollos hasta espacios íntimos, llevando intención y reverencia a cada proyecto.",
    emailPress: "CONTACTO / PRENSA",
    email: "contact@unique-sada.works",
    tempUnit: "°C",
    contactFormTitle: "Contactar a Unique.",
    nameLabel: "Nombre",
    pronounsLabel: "Pronombres",
    emailLabel: "Correo electrónico",
    descriptionLabel: "Descripción",
    submitButton: "Enviar",
    cancelButton: "Cancelar",
    weatherConditions: {
      "clear": "Despejado",
      "sunny": "Soleado",
      "partly cloudy": "Parcialmente nublado",
      "cloudy": "Nublado",
      "overcast": "Cubierto",
      "mist": "Niebla",
      "patchy rain possible": "Posibilidad de lluvia dispersa",
      "light rain": "Lluvia ligera",
      "light rain shower": "Lluvia ligera",
      "patchy light rain": "Lluvia ligera",
    },
    read: "LEER",
    listen: "ESCUCHAR",
    sendingLabel: "Enviando…",
    sendError: "Hubo un problema al enviar tu mensaje. Inténtalo de nuevo.",
    validationRequired: "Por favor completa tu nombre, correo electrónico y mensaje.",
    validationEmail: "Por favor ingresa un correo electrónico válido.",
  },

  ja: {
    name: "ユニーク・サダ",
    infoTitle: "情報",
    jobTitle: "建築・デザイン・空間の多様性",
    aboutText1:
      "ユニーク・サダの活動は、空間デザイン、文化的記憶、そして集合的自律性の交差点で展開されています。建築環境は決して中立ではないという信念に基づき、彼女の仕事は建築や都市計画を脱植民地化し、白人中心の空間論理を解体しながら、先住民の生態系や有色人種のコミュニティを再び中心に据えることを目指しています。",
    aboutText2:
      "文化保存や社会的つながりの伝統を踏まえ、彼女は空間を単なる物理的インフラではなく、歴史・願望・未来を刻む生きたアーカイブとして捉えています。都市空間を再構想する中で、デザインが物質的な境界を超え、空間や建築、日常的な営みを通じて、帰属意識と修復を形作る可能性を探ります。",
    aboutText3:
      "現在、ユニークはロイヤル・カレッジ・オブ・アートの建築修士課程（デザイン・プラクティス）に在籍し、素材の実験とデザインの革新に取り組んでいます。また、南カリフォルニア大学で都市計画の修士号を取得し、建築環境デザインを専攻しました。彼女の仕事は公共部門から民間部門まで幅広く、大規模開発から親密なスケールの空間まで、すべてのプロジェクトに意図と敬意を込めています。",
    emailPress: "お問い合わせ / プレス",
    email: "contact@unique-sada.works",
    tempUnit: "°C",
    contactFormTitle: "ユニークに連絡する",
    nameLabel: "名前",
    pronounsLabel: "代名詞",
    emailLabel: "メールアドレス",
    descriptionLabel: "内容",
    submitButton: "送信",
    cancelButton: "キャンセル",
    weatherConditions: {
      "clear": "快晴",
      "sunny": "晴れ",
      "partly cloudy": "晴れ時々曇り",
      "cloudy": "曇り",
      "overcast": "曇天",
      "mist": "霧",
      "patchy rain possible": "にわか雨の可能性",
      "light rain": "小雨",
      "light rain shower": "小雨",
      "patchy light rain": "小雨",
    },
    read: "読む",
    listen: "聞く",
    sendingLabel: "送信中…",
    sendError: "メッセージの送信中に問題が発生しました。もう一度お試しください。",
    validationRequired: "お名前、メールアドレス、内容をご入力ください。",
    validationEmail: "有効なメールアドレスをご入力ください。",
  },
};

// ---------- Public API ----------

export const translate = (
  textKey: keyof Translation,
  language: Lang
): string => {
  return translations[language][textKey];
};

// Normalize the provider condition string to improve matching
const normalizeCondition = (condition: string) =>
  (condition || "").trim().toLowerCase();

export const translateWeatherCondition = (
  condition: string,
  language: Lang
): string => {
  const key = normalizeCondition(condition);
  const mapped = translations[language].weatherConditions[key];
  // Fallback to the original provider string if we don't have a match
  return mapped ?? condition;
};
